import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import RxFollowUpService from "../../services/rx-follow-up.service";
import "./index.scss";

// Requests with no Rx number are grouped under a "Request #123" heading that reads on its own.
const sectionHeading = (rxNumber) =>
  `${rxNumber}`.startsWith("Request #") ? rxNumber : `Rx ${rxNumber}`;

// A term is either an Rx number or a transfer request number, so a typed "#" is dropped.
const parseTerms = (value) =>
  Array.from(
    new Set(
      `${value || ""}`
        .split(/[\s,;]+/)
        .map((item) => item.trim().replace(/^#+/, ""))
        .filter(Boolean)
    )
  );

const RxFollowUpReport = () => {
  const [termInput, setTermInput] = useState("");
  const [terms, setTerms] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    document.title = "TransferMyRx::Rx Follow-up Report";
    const pageTitle = document.getElementById("page-title-text");
    if (pageTitle) {
      pageTitle.innerText = "Rx Follow-up Report";
    }
  }, []);

  const addTerms = () => {
    const parsed = parseTerms(termInput);
    if (!parsed.length) return;
    setTerms((current) => Array.from(new Set([...current, ...parsed])));
    setTermInput("");
    setSearchResult(null);
  };

  const removeTerm = (term) => {
    setTerms((current) => current.filter((item) => item !== term));
    setSearchResult(null);
  };

  const findHandler = async () => {
    const parsed = Array.from(new Set([...terms, ...parseTerms(termInput)]));
    if (!parsed.length) return;

    setTerms(parsed);
    setTermInput("");
    setIsSearching(true);
    const response = await RxFollowUpService.SearchReport(parsed);
    setIsSearching(false);

    const result = response?.data || { sections: [], unmatchedTerms: parsed };
    setSearchResult(result);
    setSelectedRequestIds(
      (result.sections || []).flatMap((section) =>
        section.requests.map((request) => Number(request.RequestId))
      )
    );
  };

  const toggleRequest = (requestId) => {
    setSelectedRequestIds((current) =>
      current.includes(requestId)
        ? current.filter((id) => id !== requestId)
        : [...current, requestId]
    );
  };

  const generateHandler = async () => {
    if (!selectedRequestIds.length) return;
    setIsGenerating(true);
    try {
      await RxFollowUpService.DownloadReportPdf(selectedRequestIds);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Could not generate the report" });
    }
    setIsGenerating(false);
  };

  const totalFollowUps = useMemo(
    () =>
      (searchResult?.sections || [])
        .flatMap((section) => section.requests)
        .filter((request) => selectedRequestIds.includes(Number(request.RequestId)))
        .reduce((total, request) => total + Number(request.FollowUpCount ?? 0), 0),
    [searchResult, selectedRequestIds]
  );

  return (
    <div className="dashboard-container rx-follow-up-report">
      <div className="card mb-3">
        <div className="card-header">Rx Numbers or Request #</div>
        <div className="card-body">
          <p className="text-muted">
            Paste or type the Rx numbers or transfer request numbers you want in the report,
            separated by commas, spaces or new lines. Every follow-up recorded on the matching
            transfer requests is grouped by Rx number into a single PDF.
          </p>

          <div className="rx-follow-up-report__input-row">
            <textarea
              className="form-control"
              rows={3}
              placeholder="e.g. 1234567, RX-0012311, #17859"
              value={termInput}
              onChange={(event) => setTermInput(event.target.value)}
              onBlur={addTerms}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={findHandler}
              disabled={isSearching || (!terms.length && !parseTerms(termInput).length)}
            >
              {isSearching ? "Searching..." : "Find"}
            </button>
          </div>

          {terms.length > 0 && (
            <div className="rx-follow-up-report__chips">
              {terms.map((term) => (
                <span className="rx-follow-up-report__chip" key={term}>
                  {term}
                  <button
                    type="button"
                    className="btn-close btn-close-sm ms-2"
                    aria-label={`Remove ${term}`}
                    onClick={() => removeTerm(term)}
                  ></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {searchResult && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              Preview — {selectedRequestIds.length} request(s), {totalFollowUps} follow-up(s)
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={generateHandler}
              disabled={isGenerating || !selectedRequestIds.length}
            >
              <i className="fa-solid fa-file-pdf me-2"></i>
              {isGenerating ? "Generating..." : "Generate PDF"}
            </button>
          </div>
          <div className="card-body">
            {searchResult.unmatchedTerms?.length > 0 && (
              <div className="alert alert-warning">
                No transfer request you have access to matches:{" "}
                <strong>{searchResult.unmatchedTerms.join(", ")}</strong>
              </div>
            )}

            {searchResult.sections?.length === 0 && (
              <p className="text-muted mb-0">Nothing to report for what you entered.</p>
            )}

            {(searchResult.sections || []).map((section) => (
              <div className="rx-follow-up-report__section" key={section.rxNumber}>
                <h5>{sectionHeading(section.rxNumber)}</h5>

                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Request</th>
                      <th>Date</th>
                      <th>Drug</th>
                      <th>Status</th>
                      <th>Follow-ups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.requests.map((request) => (
                      <tr key={request.RequestId}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedRequestIds.includes(Number(request.RequestId))}
                            onChange={() => toggleRequest(Number(request.RequestId))}
                          />
                        </td>
                        <td>#{request.RequestId}</td>
                        <td>
                          {request.RequestDate
                            ? moment(request.RequestDate).format("MMM DD, YYYY")
                            : "-"}
                        </td>
                        <td>{request.DispensedDrugName || request.PrescribedDrugName || "-"}</td>
                        <td>{request.StatusName || "-"}</td>
                        <td>
                          {Number(request.FollowUpCount ?? 0) === 0 ? (
                            <span className="text-muted">No follow-up recorded</span>
                          ) : (
                            request.FollowUpCount
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RxFollowUpReport;
